import React, { useState, useRef, useLayoutEffect, useEffect, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';

// ============================================================================
// TYPES
// ============================================================================

export interface ActionMenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

export interface ActionMenuProps {
  /** Unique identifier for the menu */
  id?: string;
  /** Array of menu items to display */
  items: ActionMenuItem[];
  /** Custom trigger button content */
  triggerContent?: ReactNode;
  /** Additional class name for the trigger button */
  triggerClassName?: string;
  /** Menu position preference */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /** Callback when menu opens/closes */
  onOpenChange?: (isOpen: boolean) => void;
  /** Disabled state */
  disabled?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * ActionMenu - A reusable dropdown menu component that uses React Portal
 * to render outside of parent containers, avoiding overflow clipping issues.
 *
 * @example
 * ```tsx
 * <ActionMenu
 *   items={[
 *     { id: 'view', label: 'View', icon: <EyeIcon />, onClick: () => handleView() },
 *     { id: 'edit', label: 'Edit', icon: <EditIcon />, onClick: () => handleEdit() },
 *     { id: 'delete', label: 'Delete', icon: <TrashIcon />, onClick: () => handleDelete(), variant: 'danger' },
 *   ]}
 * />
 * ```
 */
export const ActionMenu: React.FC<ActionMenuProps> = ({
  id,
  items,
  triggerContent,
  triggerClassName = '',
  position = 'bottom-right',
  onOpenChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Phase 1: Initial position — always open below the trigger.
  // useLayoutEffect (phase 2) will flip upward if it overflows.
  const calculatePosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = 180;
    const viewportWidth = window.innerWidth;
    const spaceRight = viewportWidth - rect.right;

    // Always start below the trigger
    const top = rect.bottom + 4;

    // Determine horizontal position
    const shouldOpenLeft = position.endsWith('left') || spaceRight < menuWidth;
    let left: number;
    if (shouldOpenLeft) {
      left = Math.max(8, rect.left);
    } else {
      left = Math.max(8, rect.right - menuWidth);
    }
    left = Math.max(8, Math.min(left, viewportWidth - menuWidth - 8));

    setMenuPosition({ top, left });
  };

  // Phase 2: After menu renders, measure actual size and adjust if it overflows
  useLayoutEffect(() => {
    if (isOpen && menuRef.current && triggerRef.current) {
      const menuRect = menuRef.current.getBoundingClientRect();
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let newLeft = menuPosition.left;
      let newTop = menuPosition.top;
      let adjusted = false;

      // Horizontal: prevent overflow right
      if (menuRect.right > vw - 8) {
        newLeft = Math.max(8, vw - menuRect.width - 8);
        adjusted = true;
      }

      // Vertical: if menu overflows bottom, flip above the trigger
      if (menuRect.bottom > vh - 8) {
        newTop = triggerRect.top - menuRect.height - 4;
        // If that goes above viewport, clamp to top
        if (newTop < 8) {
          newTop = 8;
        }
        adjusted = true;
      }

      if (adjusted) {
        setMenuPosition({ top: newTop, left: newLeft });
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  // Close menu on window resize or scroll so stale positions don't persist
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [isOpen, closeMenu]);

  const openMenu = () => {
    if (disabled) return;
    calculatePosition();
    setIsOpen(true);
    onOpenChange?.(true);
  };

  const handleItemClick = (item: ActionMenuItem) => {
    if (item.disabled) return;
    item.onClick();
    closeMenu();
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  return (
    <div className="action-menu-container" id={id}>
      <button
        ref={triggerRef}
        className={`action-menu-trigger ${isOpen ? 'active' : ''} ${triggerClassName}`}
        onClick={handleTriggerClick}
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {triggerContent || <IconSystem.moreVertical size={16} />}
      </button>

      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div
            onClick={closeMenu}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998,
              background: 'transparent',
            }}
            aria-hidden="true"
          />
          {/* Menu */}
          <div
            ref={menuRef}
            role="menu"
            className="action-menu-dropdown"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: menuPosition.top,
              left: menuPosition.left,
              zIndex: 9999,
            }}
          >
            {items.map((item, index) => {
              // Check if we should add a divider before this item
              const prevItem = items[index - 1];
              const showDivider = prevItem && prevItem.variant !== 'danger' && item.variant === 'danger';

              return (
                <React.Fragment key={item.id}>
                  {showDivider && <div className="action-menu-divider" />}
                  <button
                    role="menuitem"
                    className={`action-menu-item ${item.variant === 'danger' ? 'action-menu-item-danger' : ''}`}
                    onClick={() => handleItemClick(item)}
                    disabled={item.disabled}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default ActionMenu;
